/**
 * CORRENTE DE ARMAS (10.5H) — até N armas da BOLSA ficam PRESAS ao Hunter por elos de
 * energia, orbitando; cada uma ATACA sozinha (no ritmo da própria arma) qualquer inimigo
 * até `reach` metros — a corrente "estica" a arma até o alvo (acerta voadores também).
 * As armas são escolhidas automaticamente (melhor DPS da bolsa, fora a da mão).
 */
import * as THREE from "three";
import { quality } from "../core/Quality";
import { GameConfig } from "../config/GameConfig";
import { definitionById, isWeaponDef } from "../items/Catalog";
import { weaponBaseDamage, weaponDps, effectiveRollValue } from "../items/Stats";
import { RARITY_COLOR, rarityIndex } from "../items/Rarity";
import { events } from "../core/Events";
import { dealDamage } from "../combat/DamageSystem";
import { wallBetween } from "../world/TerritoryGates";
import { buildWeaponMesh } from "./Player";
import type { ProjectileManager } from "../combat/ProjectileManager";
import type { Damageable } from "../combat/Damageable";
import type { CombatStats } from "../combat/CombatStats";
import type { EquipmentInstance, WeaponDefinition } from "../items/EquipmentTypes";

interface Entry {
  inst: EquipmentInstance;
  def: WeaponDefinition;
  mesh: THREE.Object3D;
  links: THREE.Mesh[];
  cooldown: number;
  strike: number; // 0 = em órbita; >0 = tempo restante do golpe (ida e volta)
  target: Damageable | null;
  baseDamage: number;
  restPos: THREE.Vector3;
  fresh: boolean; // 1º frame: nasce já na órbita (não desliza da origem)
  /** 10.6D — arma de DISPARO (arco). Não voa até o alvo: fica parada e atira flecha. */
  ranged: boolean;
  recoil: number; // s restantes do coice do disparo (só ranged)
  aimY: number; // ângulo atual da mira (suavizado, só ranged)
  /** 10.9GS — rastro de lâmina (fita aditiva com as últimas posições da arma); null = qualidade baixa/arco */
  trail: THREE.Mesh | null;
  trailPts: Float32Array;
  trailOn: number; // 0..1 — acende no golpe, apaga ao voltar
}

/** 10.9GS — pontos do rastro (mais = rastro mais longo/suave). */
const TRAIL_N = 9;
function makeTrail(color: number): THREE.Mesh {
  const geo = new THREE.BufferGeometry();
  const idx: number[] = [];
  for (let i = 0; i < TRAIL_N - 1; i++) { const a = i * 2, b = a + 1, c = a + 2, d = a + 3; idx.push(a, b, c, b, d, c); }
  geo.setIndex(idx);
  geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(TRAIL_N * 2 * 3), 3));
  geo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(TRAIL_N * 2 * 3), 3));
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, vertexColors: true });
  const m = new THREE.Mesh(geo, mat);
  m.frustumCulled = false;
  m.visible = false;
  return m;
}

const cfg = GameConfig.chain;
// 10.5J — FORMAÇÃO presa ao Hunter (x = direita, z = atrás), em vez de orbitar em círculo
const FORMATION = [
  { x: -0.9, z: 0.55 },
  { x: 0.9, z: 0.55 },
  { x: -1.45, z: 1.15 },
  { x: 1.45, z: 1.15 },
];
const _v = new THREE.Vector3();
const _from = new THREE.Vector3();
const _to = new THREE.Vector3();
/** 10.6D — duração do coice do arco (recuo curto ao soltar a flecha). */
const RECOIL_TIME = 0.22;
/** Interpola ângulos pelo caminho curto (senão o arco gira o "lado errado" ao trocar de alvo). */
function angleLerp(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export class ChainWeapons {
  readonly group = new THREE.Group(); // 10.9GX: público (prévia 3D)
  private entries: Entry[] = [];
  private t = 0;
  // 10.5P — poder herdado do player (ver setPower)
  private flatBase = 0;
  private dmgMult = 1;
  private apsMult = 1;
  /** 10.6D — armas de disparo presas na corrente usam o mesmo sistema de projétil do Caçador. */
  private projectiles: ProjectileManager | null = null;
  private readonly linkGeo = new THREE.OctahedronGeometry(0.07, 0);
  private readonly linkMat = new THREE.MeshStandardMaterial({ color: 0x9d7bff, emissive: 0x6a3bff, emissiveIntensity: 1.4, roughness: 0.3, flatShading: true });

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
  }

  /** 10.6D — injeta o gerenciador de projéteis (arco preso atira flecha de verdade). */
  setProjectiles(pm: ProjectileManager): void {
    this.projectiles = pm;
  }

  /** Instâncias presas (p/ a barra de slots e UI). */
  get weapons(): EquipmentInstance[] {
    return this.entries.map((e) => e.inst);
  }

  /**
   * 10.5P — PODER DO CAÇADOR nas armas da corrente. Antes cada arma presa batia só com o
   * `baseDamage` da definição dela: a arma da mão batia 200 e as da corrente 19. Agora elas
   * herdam TODOS os atributos do player (nível, altar, absorção, árvore, equipamento) —
   * o que muda de uma para a outra é só a arma em si.
   *   dano = (flatSemArma + baseDaArmaPresa) × multDano × chain.dmgMult
   * `apsMult` também entra (velocidade de ataque vale para todos os slots).
   */
  setPower(flatWithoutWeapon: number, dmgMult: number, apsMult: number): void {
    this.flatBase = Math.max(0, flatWithoutWeapon);
    this.dmgMult = Math.max(0.05, dmgMult);
    this.apsMult = Math.max(0.2, apsMult);
    for (const e of this.entries) e.baseDamage = this.damageFor(e.inst);
  }
  /** 10.9HK — dano que esta arma teria PRESA na corrente (pro card da Bolsa). */
  previewDamage(inst: EquipmentInstance): number { return this.damageFor(inst); }
  private damageFor(inst: EquipmentInstance): number {
    // 10.9L24 — soma os rolls de DANO da própria arma presa (flat "damage" + "damagePct"), senão
    // uma arma presa com atributo de dano rolado batia igual a uma sem nenhum — não era "o mesmo
    // dano da mão" de verdade, já que na mão esses rolls SEMPRE contam.
    let flatRoll = 0, pctRoll = 0;
    for (const r of inst.rolls) {
      if (r.key === "damage") flatRoll += effectiveRollValue(inst, r.value);
      else if (r.key === "damagePct") pctRoll += effectiveRollValue(inst, r.value);
    }
    const dmg = (this.flatBase + weaponBaseDamage(inst) * cfg.weaponWeight + flatRoll) * (this.dmgMult + pctRoll) * cfg.dmgMult;
    return Math.max(1, Math.round(dmg));
  }
  /** 10.5T — dano somado das presas: entra na Ameaça Crescente (senão a corrente virava
   *  poder "de graça" e os monstros comuns ficavam fáceis conforme ela crescia). */
  get powerSum(): number {
    let s = 0;
    for (const e of this.entries) s += e.baseDamage;
    return s;
  }
  /** DPS somado da corrente (para a tela de atributos). */
  get totalDps(): number {
    let s = 0;
    for (const e of this.entries) s += e.baseDamage * e.def.attacksPerSecond * this.apsMult;
    return Math.round(s);
  }

  /** Escolhe até maxWeapons melhores armas (DPS) da bolsa, exceto a da mão. Reconstrói o visual
   * só se a lista mudou.
   * 10.5N — `onlyType`: ao equipar POR TIPO (ex.: Machado), a corrente prende só armas daquele
   * tipo, do mais forte ao mais fraco. Se não houver nenhuma do tipo, cai no melhor DPS geral. */
  setFromBag(bag: EquipmentInstance[], mainHand: EquipmentInstance | null, onlyType?: string | null, maxSlots?: number, allowedTypes?: readonly string[], canUse?: (i: EquipmentInstance) => boolean): void {
    const typeOf = (i: EquipmentInstance): string | null => {
      const d = definitionById(i.definitionId);
      return d && isWeaponDef(d) ? (d as WeaponDefinition).weaponType : null;
    };
    const isWeapon = (i: EquipmentInstance): boolean => {
      const t = typeOf(i);
      if (!t || i.instanceId === mainHand?.instanceId) return false;
      // 10.9Z — a corrente só prende armas da CLASSE (nada de arma de outra classe)
      if (allowedTypes && !allowedTypes.includes(t)) return false;
      // 10.9AL — e só armas que o Caçador PODE equipar (nível mínimo por raridade; VIP ignora).
      // Antes um arco épico Nv20 entrava na corrente de um nv9 pelo botão de tipo do inventário.
      return !canUse || canUse(i);
    };
    let pool = bag.filter(isWeapon);
    if (onlyType) {
      const typed = pool.filter((i) => typeOf(i) === onlyType);
      if (typed.length > 0) pool = typed;
    }
    // 10.9W — a corrente só usa os slots DESBLOQUEADOS por nível (maxSlots). Começa com 1 arma
    // (mão) e libera cada arma presa conforme o nível (ver GameConfig.equipment.weaponSlots).
    const limit = Math.max(0, Math.min(cfg.maxWeapons, maxSlots ?? cfg.maxWeapons));
    const cands = pool.sort((a, b) => weaponDps(b) - weaponDps(a)).slice(0, limit);
    if (cands.length === this.entries.length && cands.every((c, i) => c.instanceId === this.entries[i].inst.instanceId)) return;
    this.clear();
    for (const inst of cands) {
      const def = definitionById(inst.definitionId) as WeaponDefinition;
      // 10.6E — arma presa também brilha na cor da RARIDADE dela (a barra de armas já
      // mostra a cor; agora o objeto no mundo combina com o slot).
      const mesh = buildWeaponMesh(def.visualId, { energy: RARITY_COLOR[inst.rarity], glow: 1 + rarityIndex(inst.rarity) * 0.2, tier: def.tier, family: def.id.replace(/[A-Z].*$/, "") }); // 10.9GX/GZ: skin por tier + família
      mesh.scale.multiplyScalar(0.92);
      this.group.add(mesh);
      const links: THREE.Mesh[] = [];
      for (let k = 0; k < cfg.links; k++) {
        const l = new THREE.Mesh(this.linkGeo, this.linkMat);
        this.group.add(l);
        links.push(l);
      }
      const ranged = def.attackType === "ranged"; // 10.6D
      const trail = !ranged && quality.values.particleScale >= 0.75 ? makeTrail(RARITY_COLOR[inst.rarity]) : null; // 10.9GS
      if (trail) this.group.add(trail);
      this.entries.push({
        inst, def, mesh, links,
        cooldown: Math.random() * 0.6, // desfasa os golpes entre as armas
        strike: 0, target: null,
        baseDamage: this.damageFor(inst),
        restPos: new THREE.Vector3(),
        fresh: true,
        ranged,
        recoil: 0,
        aimY: 0,
        trail, trailPts: new Float32Array(TRAIL_N * 3), trailOn: 0,
      });
    }
  }

  clear(): void {
    for (const e of this.entries) {
      this.group.remove(e.mesh);
      for (const l of e.links) this.group.remove(l);
      if (e.trail) { this.group.remove(e.trail); e.trail.geometry.dispose(); (e.trail.material as THREE.Material).dispose(); } // 10.9GS
    }
    this.entries = [];
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  update(dt: number, playerPos: THREE.Vector3, facing: number, playerStats: CombatStats, enemies: readonly Damageable[], combatEnabled: boolean): void {
    const n = this.entries.length;
    if (n === 0) return;
    this.t += dt;
    const fx = Math.sin(facing), fz = Math.cos(facing); // frente do Hunter
    const rx = fz, rz = -fx; // direita
    for (let i = 0; i < n; i++) {
      const e = this.entries[i];
      // repouso: slot fixo da formação atrás/ao lado do Hunter (gira com ele), flutuando
      const slot = FORMATION[i % FORMATION.length];
      e.restPos.set(
        playerPos.x + rx * slot.x - fx * slot.z,
        cfg.height + Math.sin(this.t * 2.2 + i) * 0.1,
        playerPos.z + rz * slot.x - fz * slot.z,
      );
      if (e.fresh) { e.mesh.position.copy(e.restPos); e.fresh = false; }

      // ataque autônomo (respeita safe zone via combatEnabled)
      e.cooldown -= dt;
      if (combatEnabled && e.cooldown <= 0 && (e.ranged || e.strike <= 0)) {
        const tgt = this.nearest(playerPos, enemies, this.rangeOf(e));
        // 10.6Y — arma na corrente NÃO acerta inimigo se tem parede entre o Hunter e o alvo
        if (tgt && wallBetween(playerPos.x, playerPos.z, tgt.position.x, tgt.position.z)) continue;
        if (tgt) {
          e.cooldown = 1 / Math.min(GameConfig.combat.maxAttacksPerSecond, Math.max(0.15, e.def.attacksPerSecond * this.apsMult)); // 10.9S: mesmo teto de velocidade
          const stats: CombatStats = { ...playerStats, baseDamage: e.baseDamage };
          if (e.ranged) {
            // 10.6D — ARCO NA CORRENTE ATIRA. Antes toda arma presa fazia o mesmo "estalo"
            // de melee: o arco voava até o inimigo e batia nele como se fosse um porrete.
            // Agora ele fica no lugar, mira e solta FLECHA — o dano sai no impacto do
            // projétil (por isso aqui não tem dealDamage).
            e.target = tgt;
            e.recoil = RECOIL_TIME;
            _v.subVectors(tgt.position, e.mesh.position);
            _v.y = 0;
            if (_v.lengthSq() < 1e-6) _v.set(fx, 0, fz);
            _v.normalize();
            events.emit("fx:shot", { x: e.mesh.position.x + _v.x * 0.4, y: e.mesh.position.y, z: e.mesh.position.z + _v.z * 0.4, color: RARITY_COLOR[e.inst.rarity] });
            this.projectiles?.fire(e.mesh.position, _v.x, _v.z, e.def.projectileSpeed, stats, e.def.damageType, e.def.knockback * 0.6, { target: tgt, weaponType: e.def.weaponType });
          } else {
            e.target = tgt;
            e.strike = cfg.strikeTime;
            dealDamage(tgt, stats, { targetKind: "enemy", byPlayer: true, damageType: e.def.damageType, weaponType: e.def.weaponType, knockbackFrom: playerPos, knockbackForce: e.def.knockback * 0.6 });
          }
        }
      }

      // visual: arco fica parado mirando (com coice); melee vai até o alvo e volta
      if (e.ranged) {
        e.recoil = Math.max(0, e.recoil - dt);
        // mira: alvo vivo dentro do alcance, senão acompanha a frente do Caçador
        let ax = fx, az = fz;
        if (e.target && !e.target.isDead) {
          _v.subVectors(e.target.position, e.restPos);
          _v.y = 0;
          if (_v.lengthSq() > 1e-6) { _v.normalize(); ax = _v.x; az = _v.z; }
        }
        // O arco dispara na direção do seu -X local (corda em x=0.22, riser em x=0.30).
        const want = Math.atan2(az, -ax);
        e.aimY = angleLerp(e.aimY, want, Math.min(1, dt * 10));
        // coice: recua um pouco no eixo do tiro e volta (0→1→0 dentro de RECOIL_TIME)
        const k = e.recoil > 0 ? Math.sin((e.recoil / RECOIL_TIME) * Math.PI) * 0.22 : 0;
        _to.set(e.restPos.x - ax * k, e.restPos.y, e.restPos.z - az * k);
        e.mesh.position.lerp(_to, Math.min(1, dt * 16));
        e.mesh.rotation.set(0, e.aimY, 0);
      } else if (e.strike > 0 && e.target) {
        e.strike -= dt;
        const p = 1 - Math.abs((e.strike / cfg.strikeTime) * 2 - 1); // 0→1→0
        _to.copy(e.target.position);
        _v.subVectors(_to, playerPos);
        _v.y = 0;
        const d = _v.length();
        if (d > cfg.reach && d > 0.001) _v.multiplyScalar(cfg.reach / d);
        _to.set(playerPos.x + _v.x, cfg.height, playerPos.z + _v.z);
        e.mesh.position.lerpVectors(e.restPos, _to, p);
        e.mesh.rotation.set(0, Math.atan2(_v.x, _v.z), Math.PI / 2); // deita apontando pro alvo
        if (e.strike <= 0) e.target = null;
      } else {
        e.mesh.position.lerp(e.restPos, Math.min(1, dt * 8));
        e.mesh.rotation.set(0.25, e.mesh.rotation.y + dt * 1.5, 0.35); // giro suave em repouso
      }

      // 10.9GS — rastro da lâmina: fita vertical pelas últimas posições, acende só durante o golpe
      if (e.trail) {
        const striking = e.strike > 0 && !!e.target;
        e.trailOn = striking ? 1 : Math.max(0, e.trailOn - dt * 7);
        const pts = e.trailPts;
        if (e.fresh || e.trailOn <= 0) { for (let k = 0; k < TRAIL_N; k++) { pts[k * 3] = e.mesh.position.x; pts[k * 3 + 1] = e.mesh.position.y; pts[k * 3 + 2] = e.mesh.position.z; } }
        else { pts.copyWithin(3, 0, (TRAIL_N - 1) * 3); pts[0] = e.mesh.position.x; pts[1] = e.mesh.position.y; pts[2] = e.mesh.position.z; }
        if (e.trailOn > 0) {
          e.trail.visible = true;
          const pa = e.trail.geometry.attributes.position as THREE.BufferAttribute;
          const ca = e.trail.geometry.attributes.color as THREE.BufferAttribute;
          const P = pa.array as Float32Array, C = ca.array as Float32Array;
          for (let k = 0; k < TRAIL_N; k++) {
            const x = pts[k * 3], y = pts[k * 3 + 1], z = pts[k * 3 + 2];
            const o = k * 6;
            P[o] = x; P[o + 1] = y + 0.16; P[o + 2] = z;
            P[o + 3] = x; P[o + 4] = y - 0.16; P[o + 5] = z;
            const f = (1 - k / (TRAIL_N - 1)) * e.trailOn;
            C[o] = C[o + 1] = C[o + 2] = f; C[o + 3] = C[o + 4] = C[o + 5] = f * 0.6;
          }
          pa.needsUpdate = true; ca.needsUpdate = true;
        } else e.trail.visible = false;
      }

      // elos: do peito do Hunter até a arma, com leve arco
      _from.set(playerPos.x, 1.2, playerPos.z);
      for (let k = 0; k < e.links.length; k++) {
        const f = (k + 1) / (e.links.length + 1);
        const l = e.links[k];
        l.position.lerpVectors(_from, e.mesh.position, f);
        l.position.y += Math.sin(f * Math.PI) * 0.18;
        l.rotation.y = this.t * 3 + k;
      }
    }
  }

  /** Alcance de uma arma presa. Melee estica pela corrente (reach); o ARCO usa o alcance
   *  dele mesmo quando for maior — arco que só acerta a 5,5 m não é arco. */
  private rangeOf(e: Entry): number {
    // 10.6H: teto de 8,5 m — acima disso a corrente matava fora do enquadramento
    return e.ranged ? Math.min(8.5, Math.max(cfg.reach, e.def.attackRange)) : cfg.reach;
  }

  private nearest(from: THREE.Vector3, enemies: readonly Damageable[], range: number = cfg.reach): Damageable | null {
    let best: Damageable | null = null;
    let bd: number = range;
    for (const en of enemies) {
      if (!en || en.isDead) continue;
      _v.subVectors(en.position, from);
      _v.y = 0;
      const d = _v.length();
      if (d <= bd) {
        bd = d;
        best = en;
      }
    }
    return best;
  }
}
